import { parseJsonAsync, readResponseText } from '../utils/quotaUserFetch.js';

const BASE = 'https://nodeapi.monetanalytics.com/dashboard/dashboard';
// const BASE = 'http://localhost:5000/dashboard';

const QUOTA_USERS_TIMEOUT_MS = 180_000;

function formatApiError(json, status) {
  let message = json.message;
  if (Array.isArray(message)) {
    message = message.join(', ');
  } else if (message && typeof message === 'object') {
    message = JSON.stringify(message);
  }
  if (!message) message = `Request failed (${status})`;

  const detail = json.data;
  if (
    detail &&
    typeof detail === 'string' &&
    detail !== 'Bad Request' &&
    !message.includes(detail)
  ) {
    message = `${message} (${detail})`;
  }

  return message;
}

async function parseResponse(res, { onBeforeParse } = {}) {
  const text = await res.text();

  if (onBeforeParse) {
    onBeforeParse(text.length);
    // Yield so the UI can update before parsing large JSON payloads.
    await new Promise((resolve) => setTimeout(resolve, 0));
  }

  let json = {};
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = { message: text };
    }
  }

  const bodyFailed =
    json.error === true || (typeof json.code === 'number' && json.code >= 400);
  if (!res.ok || bodyFailed) {
    throw new Error(formatApiError(json, res.status));
  }

  return json;
}

async function fetchWithTimeout(url, { signal, timeoutMs, headers, ...fetchOptions } = {}) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort(new DOMException('Request timed out', 'TimeoutError'));
  }, timeoutMs ?? QUOTA_USERS_TIMEOUT_MS);

  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  try {
    return await fetch(url, {
      ...fetchOptions,
      headers: { 'Content-Type': 'application/json', ...headers },
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }
}

async function request(path, options = {}) {
  const { signal, timeoutMs, onBeforeParse, ...fetchOptions } = options;
  const res = await fetchWithTimeout(`${BASE}${path}`, { signal, timeoutMs, ...fetchOptions });
  const json = await parseResponse(res, { onBeforeParse });

  if (Object.prototype.hasOwnProperty.call(json, 'data')) {
    return json.data ?? { message: json.message };
  }
  return json;
}

/** Mutation helper that always surfaces the backend success/error message. */
async function requestMutation(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  const json = await parseResponse(res);

  return {
    data: Object.prototype.hasOwnProperty.call(json, 'data') ? json.data : json,
    message: json.message || '',
  };
}

// ── Language APIs ─────────────────────────────────────────────────────────────

function normalizeLanguageData(data) {
  const lang = data.language ?? data.languages ?? [];
  return {
    languages: Array.isArray(lang) ? lang : [lang],
    language_modules: data.languageModule ?? data.language_modules ?? [],
    que_cards: data.que_cards ?? data.queCards ?? [],
  };
}

export const getLanguage = async (l_id) => {
  const data = await request(`/get-language?l_id=${l_id}`);
  return normalizeLanguageData(data);
};

export const cloneLanguage = (l_id, l_name) =>
  request('/clone-language', {
    method: 'POST',
    body: JSON.stringify({ l_id: Number(l_id), l_name }),
  });

// FIX #2: always send { [idKey]: idValue, update } so q_id / lm_id / l_id
// are all present in the payload — not just the update object.
export const updateLanguage = (idKey, idValue, update) =>
  request('/update-languages', {
    method: 'POST',
    body: JSON.stringify({ [idKey]: idValue, update }),
  });

export const deleteLanguage = (idKey, idValue) =>
  request('/delete-language', {
    method: 'POST',
    body: JSON.stringify({ [idKey]: idValue }),
  });

// FIX #1: Add Language Module
export const addLanguageModule = (payload) =>
  request('/add-language-module', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

// FIX #1: Add Que Card
export const addQueCard = (payload) =>
  request('/add-que-card', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

// ── Landing Page APIs ─────────────────────────────────────────────────────────

function normalizeLandingPageData(data) {
  const land = data.landingPage ?? data.landing_page ?? data.landing_pages ?? [];
  return {
    landing_pages: Array.isArray(land) ? land : [land],
    landing_survey_questions:
      data.landingSurveyQuestions ?? data.landing_survey_questions ?? data.landingSurveyQuestion ?? [],
  };
}

export const getLandingPage = async (lp_id) => {
  const data = await request(`/get-landing-page?lp_id=${lp_id}`);
  return normalizeLandingPageData(data);
};

export const cloneLandingPage = (lp_id) =>
  request('/clone-landing-page', {
    method: 'POST',
    body: JSON.stringify({ lp_id: Number(lp_id) }),
  });

export const updateLandingPage = (idKey, idValue, update) =>
  request('/update-landing-page', {
    method: 'POST',
    body: JSON.stringify({ lp_id: idValue, update }),
  });

export const updateLandingSurveyQuestion = (idKey, idValue, update) =>
  request('/update-landing-page', {
    method: 'POST',
    body: JSON.stringify({ id: idValue, update }),
  });

export const deleteLandingPage = (idKey, idValue) =>
  request('/delete-landing-page', {
    method: 'POST',
    body: JSON.stringify({ [idKey]: idValue }),
  });

export const addLandingSurveyQuestion = (payload) =>
  request('/add-landing-survey-question', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

// ── Over Quota Users API ──────────────────────────────────────────────────────

function normalizeOverQuotaUsersData(data) {
  if (Array.isArray(data)) {
    return { quota_catalog: {}, users: data };
  }

  return {
    quota_catalog: data?.quota_catalog ?? {},
    users: Array.isArray(data?.users) ? data.users : [],
  };
}

async function requestQuotaUsers(path, options = {}) {
  const { signal, timeoutMs, onDownloadProgress, onProcessing } = options;
  const res = await fetchWithTimeout(`${BASE}${path}`, { signal, timeoutMs });

  onProcessing?.();
  const text = await readResponseText(res, onDownloadProgress);
  const json = await parseJsonAsync(text);

  const bodyFailed =
    json.error === true || (typeof json.code === 'number' && json.code >= 400);
  if (!res.ok || bodyFailed) {
    throw new Error(formatApiError(json, res.status));
  }

  const data = Object.prototype.hasOwnProperty.call(json, 'data')
    ? json.data ?? { message: json.message }
    : json;

  return normalizeOverQuotaUsersData(data);
}

export const getOverQuotaUsers = async (study_id, options = {}) =>
  requestQuotaUsers(`/over-quota-users?study_id=${study_id}`, {
    timeoutMs: QUOTA_USERS_TIMEOUT_MS,
    ...options,
  });

export const getReassignQuotaUsers = async (study_id, options = {}) =>
  requestQuotaUsers(`/reassign-quota-users?study_id=${study_id}`, {
    timeoutMs: QUOTA_USERS_TIMEOUT_MS,
    ...options,
  });

export const reviveOverQuotaUsers = (wh_mo_ids) =>
  requestMutation('/revive-over-quota-users', {
    method: 'POST',
    body: JSON.stringify({ wh_mo_ids }),
  });

/** Updates watch_history quota IDs for one user, then runs the revive flow. */
export const reassignAndReviveOverQuotaUser = (payload) =>
  requestMutation('/reassign-and-revive-over-quota-user', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

/** Replace from_cnq_id with to_cnq_id on multiple over-quota users. */
export const bulkSwapQuota = (payload) =>
  requestMutation('/bulk-swap-quota', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

// ── Dashboard URLs API ────────────────────────────────────────────────────────

export const getDashboardUrls = async () => {
  const data = await request('/dashboard-urls');
  if (Array.isArray(data)) return data;
  return Array.isArray(data?.urls) ? data.urls : [];
};

export const createDashboardUrl = (payload) =>
  requestMutation('/create-dashboard-url', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
